import type { RefObject } from "react";
import { invariant } from "@searchui-searchforms/shared/utils";
import { useOnAddField } from "@/features/form-fields";
import type { FieldPath, FormAPI } from "@/shared/forms";
import { createEventInstance, createFamilyMemberInstance } from "../form";
import type { FormValues, SubmitMeta } from "../form";
import type { MatchAllTermsProviderRef } from "../match-all-terms";

interface UseOnAddFieldHandlerArgs {
    formRef: RefObject<FormAPI<FormValues, SubmitMeta> | null>;
    matchAllTermsRef: RefObject<MatchAllTermsProviderRef | null>;
    defaultYearProximity: number | null;
}

/** The new row is exact only while match-all-terms is supported and switched on, matching seeding's rule. */
export const useOnAddFieldHandler = ({ formRef, matchAllTermsRef, defaultYearProximity }: UseOnAddFieldHandlerArgs) => {
    useOnAddField(({ fieldName, coupledFieldNames = [], type, newItemIndex, mappedAssertion }) => {
        const formApi = formRef.current;
        invariant(formApi, "Form ref is not initialized");
        const matchAllTermsExactly = matchAllTermsRef.current?.matchAllTermsExactly ?? false;
        const matchAllTermsSupported = matchAllTermsRef.current?.matchAllTermsSupported ?? false;
        const exact = matchAllTermsSupported && matchAllTermsExactly;
        if (type === "event") {
            // Every entry the row touches gains an instance, so the row is never half-populated.
            for (const path of [fieldName, ...coupledFieldNames]) {
                formApi.appendArrayItem(
                    path as FieldPath<FormValues>,
                    // @ts-expect-error TS can't resolve FieldValue for deeply nested array paths
                    createEventInstance({ exact, defaultYearProximity }),
                );
            }
        } else {
            formApi.appendArrayItem(fieldName as FieldPath<FormValues>, createFamilyMemberInstance({ exact }));
        }
        const focusPath = `${fieldName}.${newItemIndex}.${mappedAssertion}` as FieldPath<FormValues>;
        // Focus once the new row has rendered; the array item is not in the DOM synchronously.
        requestAnimationFrame(() => {
            formApi.setFocus(focusPath);
        });
    });
};
